/*
 * The in-console guide to the Bitbucket access token (plan P34): which token to create, where, with which permissions.
 * Source: Atlassian's "Create a repository access token", "Create a workspace access token" and "Repository access token
 * permissions" pages (read 2026-10-28). The token comes first (Hangar then discovers what it reaches), so the link opens
 * Bitbucket and the steps say where to click. Pure: no React.
 */

import type { ScmConnectionTestResult } from '@/api/schemas';

/** A repository access token reaches one repository on any Bitbucket plan; a workspace access token needs Premium. */
export type ScmTokenKind = 'repository' | 'workspace';

export interface TokenKindOption {
  kind: ScmTokenKind;
  label: string;
  detail: string;
}

export const TOKEN_KINDS: readonly TokenKindOption[] = [
  { kind: 'repository', label: 'One repository', detail: 'Repository access token · any Bitbucket plan' },
  { kind: 'workspace', label: 'Whole workspace', detail: 'Workspace access token · Bitbucket Premium only' },
];

export interface TokenScope {
  /** The permission as Bitbucket's create form groups it. */
  label: string;
  /** The API scope name Bitbucket shows on the token. */
  scope: string;
  why: string;
  required: boolean;
}

/** The only permissions Hangar uses; nothing else should be ticked. */
export const TOKEN_SCOPES: readonly TokenScope[] = [
  { label: 'Repositories: Read', scope: 'repository', why: 'Hangar reads the source of your builds.', required: true },
  { label: 'Webhooks: Read and write', scope: 'webhook', why: 'Hangar adds one webhook to the repository so a push can start a build.', required: false },
];

const ENTRY: Record<ScmTokenKind, string> = {
  repository: 'In Bitbucket, open the repository, select “…” next to its name in the left sidebar, then Settings.',
  workspace: 'In Bitbucket, select the Settings cog in the top bar, then Workspace settings.',
};

const CREATE: Record<ScmTokenKind, string> = {
  repository: 'Under Security, select Access tokens, then Create access token.',
  workspace: 'Under Security, select Access tokens, then Create access token. No Access tokens entry means the workspace is not on Premium: use a repository token.',
};

/** The numbered steps, from opening Bitbucket to pasting the token here. */
export const tokenSteps = (kind: ScmTokenKind): string[] => [
  ENTRY[kind],
  CREATE[kind],
  'Name: Hangar. Expiry: pick a date (at most one year is recommended) and note it: builds stop when the token expires.',
  `Permissions: tick ${TOKEN_SCOPES.map((s) => s.label).join(' and ')}. Leave everything else unticked.`,
  'Select Create and copy the token now: Bitbucket shows it only once. Paste it in Access token below: Hangar finds what it reaches.',
];

/** "Open Bitbucket": the token comes first, before Hangar knows the workspace or repository. */
export const BITBUCKET_HOME = 'https://bitbucket.org/';

/* ---------- Verify token ---------- */

export type VerifyTone = 'passed' | 'failed' | 'warning' | 'skipped';

export interface VerifyRow {
  key: string;
  label: string;
  tone: VerifyTone;
  message: string;
}

const CHECK_LABEL: Record<string, string> = {
  connectivity: 'Bitbucket answers',
  credential: 'Token accepted',
  account: 'Can read the source',
  webhooks: 'Webhooks permission (builds on push)',
};

/** A failed check that does not block (webhooks) is a warning: the token works, builds on push will not. */
function toneOf(c: ScmConnectionTestResult['checks'][number]): VerifyTone {
  if (c.status === 'passed') return 'passed';
  if (c.status === 'failed' || c.status === 'error') return c.blocking === false ? 'warning' : 'failed';
  return 'skipped';
}

/** The rows of "Verify token", in the API's order, with people's labels. */
export const verifyRows = (r: ScmConnectionTestResult): VerifyRow[] =>
  r.checks.map((c) => ({ key: c.key, label: CHECK_LABEL[c.key] ?? c.key, tone: toneOf(c), message: c.message ?? '' }));

/** The one-line verdict above the rows. */
export function verifyVerdict(r: ScmConnectionTestResult): { tone: VerifyTone; text: string } {
  const target = r.account?.externalAccount ?? 'the source';
  if (!r.ok) return { tone: 'failed', text: 'This token cannot be used: fix what failed below, or create a new token.' };
  if (verifyRows(r).some((row) => row.tone === 'warning')) return { tone: 'warning', text: `The token can read ${target}, but builds on push need the Webhooks permission.` };
  return { tone: 'passed', text: `The token is valid and can read ${target}.` };
}

/** The values a verification answered for: a change to any of them makes the shown result stale. */
export const verifyKey = (v: { kind: ScmTokenKind; workspace: string; repository: string; token: string }): string =>
  [v.kind, v.workspace.trim(), v.kind === 'repository' ? v.repository.trim() : '', v.token.trim()].join('\u0000');

/** The connection name Hangar proposes once Bitbucket answered: "Bitbucket · <its name for the repository or workspace>". */
export function proposedName(r: ScmConnectionTestResult): string | undefined {
  if (!r.ok) return undefined;
  const name = r.account?.displayName?.trim() || r.account?.externalAccount?.trim();
  return name ? `Bitbucket · ${name}`.slice(0, 120) : undefined;
}
